import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  Badge,
  FormControl,
  FormLabel,
  Input,
  Select,
  SimpleGrid,
  Divider,
  Alert,
  AlertIcon,
  Switch,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useToast,
  useColorModeValue,
} from '@chakra-ui/react';
import { FiPrinter, FiCheckCircle, FiXCircle } from 'react-icons/fi';
import { doc, onSnapshot, setDoc, updateDoc, collection, query, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functionsUsEast1 } from '../../../utils/init-firebase';

const EMPTY_ADDRESS = {
  firstName: '',
  lastName: '',
  company: '',
  address: '',
  address2: '',
  city: '',
  state: '',
  zip: '',
};

function normalizeStored(addr) {
  if (!addr || typeof addr !== 'object') return { ...EMPTY_ADDRESS };
  return {
    firstName: addr.firstName || '',
    lastName: addr.lastName || '',
    company: addr.company || '',
    address: addr.address || addr.street || '',
    address2: addr.address2 || '',
    city: addr.city || '',
    state: addr.state || '',
    zip: addr.zip || addr.zipCode || '',
  };
}

function ReturnAddressFields({ value, onChange, disabled }) {
  const set = (key) => (e) => onChange({ ...value, [key]: e.target.value });
  return (
    <VStack spacing={3} align="stretch">
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
        <FormControl>
          <FormLabel fontSize="sm">First Name</FormLabel>
          <Input size="sm" value={value.firstName} onChange={set('firstName')} isDisabled={disabled} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Last Name</FormLabel>
          <Input size="sm" value={value.lastName} onChange={set('lastName')} isDisabled={disabled} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Company</FormLabel>
          <Input size="sm" value={value.company} onChange={set('company')} isDisabled={disabled} />
        </FormControl>
      </SimpleGrid>
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
        <FormControl isRequired>
          <FormLabel fontSize="sm">Street Address</FormLabel>
          <Input size="sm" value={value.address} onChange={set('address')} isDisabled={disabled} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Address 2</FormLabel>
          <Input size="sm" value={value.address2} onChange={set('address2')} isDisabled={disabled} />
        </FormControl>
      </SimpleGrid>
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
        <FormControl isRequired>
          <FormLabel fontSize="sm">City</FormLabel>
          <Input size="sm" value={value.city} onChange={set('city')} isDisabled={disabled} />
        </FormControl>
        <FormControl isRequired>
          <FormLabel fontSize="sm">State</FormLabel>
          <Input size="sm" value={value.state} onChange={set('state')} maxLength={2} placeholder="FL" isDisabled={disabled} />
        </FormControl>
        <FormControl isRequired>
          <FormLabel fontSize="sm">ZIP</FormLabel>
          <Input size="sm" value={value.zip} onChange={set('zip')} isDisabled={disabled} />
        </FormControl>
      </SimpleGrid>
    </VStack>
  );
}

/**
 * PostcardMania settings panel (IntegrationsPage).
 *
 * - Everyone: personal return-address override (users/{uid}.postcardReturnAddress).
 * - Admins: environment toggle (sandbox/production), default mail class, and
 *   the company return address — stored at config/crmSettings.postcardMania.
 * - Admins: connection test (testPostcardConnection) showing balance + designs.
 *
 * API keys are NOT entered here: they live in Firebase Secret Manager
 * (PCM_API_KEY / PCM_API_SECRET / PCM_SANDBOX_API_KEY / PCM_SANDBOX_API_SECRET).
 */
export default function PostcardManiaSettings({ userId, isAdmin = false }) {
  // All hooks unconditionally at the top (Rules of Hooks).
  const [settings, setSettings] = useState({ environment: 'sandbox', defaultMailClass: 'FirstClass' });
  const [companyAddress, setCompanyAddress] = useState({ ...EMPTY_ADDRESS });
  const [personalAddress, setPersonalAddress] = useState({ ...EMPTY_ADDRESS });
  const [savingCompany, setSavingCompany] = useState(false);
  const [savingPersonal, setSavingPersonal] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [crmUsers, setCrmUsers] = useState([]);
  const toast = useToast();
  const sectionBg = useColorModeValue('gray.50', 'gray.700');

  // Company settings (config/crmSettings.postcardMania)
  useEffect(() => {
    const unsubscribe = onSnapshot(doc(db, 'config', 'crmSettings'), (snap) => {
      const pcm = snap.exists() ? snap.data()?.postcardMania : null;
      if (pcm) {
        setSettings({
          environment: pcm.environment === 'production' ? 'production' : 'sandbox',
          defaultMailClass: pcm.defaultMailClass === 'Standard' ? 'Standard' : 'FirstClass',
        });
        setCompanyAddress(normalizeStored(pcm.returnAddress));
      }
    });
    return () => unsubscribe();
  }, []);

  // Personal return address (users/{uid}.postcardReturnAddress)
  useEffect(() => {
    if (!userId) return undefined;
    const unsubscribe = onSnapshot(doc(db, 'users', userId), (snap) => {
      setPersonalAddress(normalizeStored(snap.exists() ? snap.data()?.postcardReturnAddress : null));
    });
    return () => unsubscribe();
  }, [userId]);

  // Admin-only: CRM user roster for the per-user access toggles.
  useEffect(() => {
    if (!isAdmin) return undefined;
    const q = query(collection(db, 'users'), where('CRMUser', '==', true));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const users = [];
      snapshot.forEach((userDoc) => {
        const u = userDoc.data();
        users.push({
          uid: userDoc.id,
          name: u.Name || u.name || u.displayName || u.Email || u.email || userDoc.id,
          email: u.Email || u.email || '',
          postcardEnabled: u.postcardEnabled === true,
          isCrmAdmin:
            u.SuperAdmin === true ||
            u.superAdmin === true ||
            u.CRMRole === 'CRM Admin' ||
            (Array.isArray(u.Roles) && u.Roles.includes('CRM Admin')),
        });
      });
      users.sort((a, b) => a.name.localeCompare(b.name));
      setCrmUsers(users);
    });
    return () => unsubscribe();
  }, [isAdmin]);

  const togglePostcardAccess = async (user, checked) => {
    try {
      await updateDoc(doc(db, 'users', user.uid), { postcardEnabled: checked });
      toast({
        title: checked ? 'Postcard access granted' : 'Postcard access removed',
        description: checked
          ? `${user.name} can now send postcards from bulk actions and campaigns`
          : `${user.name} no longer has access to PostcardMania`,
        status: 'success',
        duration: 3000,
      });
    } catch (err) {
      toast({ title: 'Error updating access', description: err.message, status: 'error', duration: 5000 });
    }
  };

  const saveCompany = async () => {
    setSavingCompany(true);
    try {
      await setDoc(
        doc(db, 'config', 'crmSettings'),
        {
          postcardMania: {
            environment: settings.environment,
            defaultMailClass: settings.defaultMailClass,
            returnAddress: companyAddress,
          },
        },
        { merge: true }
      );
      toast({ title: 'PostcardMania settings saved', status: 'success', duration: 3000 });
    } catch (err) {
      toast({ title: 'Save failed', description: err.message, status: 'error', duration: 5000 });
    } finally {
      setSavingCompany(false);
    }
  };

  const savePersonal = async () => {
    if (!userId) return;
    setSavingPersonal(true);
    try {
      await setDoc(doc(db, 'users', userId), { postcardReturnAddress: personalAddress }, { merge: true });
      toast({ title: 'Return address saved', status: 'success', duration: 3000 });
    } catch (err) {
      toast({ title: 'Save failed', description: err.message, status: 'error', duration: 5000 });
    } finally {
      setSavingPersonal(false);
    }
  };

  const runTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const fn = httpsCallable(functionsUsEast1, 'testPostcardConnection');
      const { data } = await fn({});
      setTestResult(data);
    } catch (err) {
      setTestResult({ ok: false, error: err.message });
    } finally {
      setTesting(false);
    }
  };

  const isProduction = settings.environment === 'production';

  return (
    <VStack align="stretch" spacing={6}>
      <HStack justify="space-between" flexWrap="wrap" gap={2}>
        <HStack spacing={3}>
          <Box as={FiPrinter} boxSize={6} color="green.500" />
          <Box>
            <Heading size="md">PostcardMania</Heading>
            <Text fontSize="sm" color="gray.500">
              Physical postcards from campaigns and bulk actions, printed and mailed by PostcardMania.
            </Text>
          </Box>
        </HStack>
        <Badge colorScheme={isProduction ? 'green' : 'orange'} px={2} py={1}>
          {isProduction ? 'Production — real mail' : 'Sandbox — test only'}
        </Badge>
      </HStack>

      {isAdmin && (
        <Box bg={sectionBg} borderRadius="lg" p={4}>
          <Heading size="sm" mb={1}>Connection</Heading>
          <Text fontSize="xs" color="gray.500" mb={3}>
            API keys are managed in Firebase Secret Manager (PCM_API_KEY, PCM_API_SECRET,
            PCM_SANDBOX_API_KEY, PCM_SANDBOX_API_SECRET) — not stored here.
          </Text>
          <HStack spacing={4} align="center" flexWrap="wrap">
            <Button size="sm" onClick={runTest} isLoading={testing} loadingText="Testing...">
              Test Connection
            </Button>
            {testResult && (
              testResult.ok ? (
                <HStack spacing={2} fontSize="sm">
                  <Box as={FiCheckCircle} color="green.500" />
                  <Text>
                    Connected to <b>{testResult.environment}</b>
                    {typeof testResult.balance === 'number' ? ` · Balance $${testResult.balance.toFixed(2)}` : ''}
                    {typeof testResult.designCount === 'number' ? ` · ${testResult.designCount} postcard design${testResult.designCount === 1 ? '' : 's'}` : ''}
                  </Text>
                </HStack>
              ) : (
                <HStack spacing={2} fontSize="sm">
                  <Box as={FiXCircle} color="red.500" />
                  <Text color="red.500">{testResult.error || 'Connection failed'}</Text>
                </HStack>
              )
            )}
          </HStack>
        </Box>
      )}

      {isAdmin && (
        <Box bg={sectionBg} borderRadius="lg" p={4}>
          <Heading size="sm" mb={3}>Company Settings (admin)</Heading>
          <VStack align="stretch" spacing={4}>
            <FormControl display="flex" alignItems="flex-start" justifyContent="space-between" gap={3}>
              <Box>
                <FormLabel mb={0} fontSize="sm">Production mode</FormLabel>
                <Text fontSize="xs" color="gray.500">
                  Off = Sandbox: orders are accepted but never printed or mailed. Turn on only
                  after sandbox testing — production orders cost money and mail for real.
                </Text>
              </Box>
              <Switch
                isChecked={isProduction}
                colorScheme="green"
                onChange={(e) => setSettings((s) => ({ ...s, environment: e.target.checked ? 'production' : 'sandbox' }))}
              />
            </FormControl>

            <FormControl maxW="240px">
              <FormLabel fontSize="sm">Default Mail Class</FormLabel>
              <Select
                size="sm"
                value={settings.defaultMailClass}
                onChange={(e) => setSettings((s) => ({ ...s, defaultMailClass: e.target.value }))}
              >
                <option value="FirstClass">First Class</option>
                <option value="Standard">Standard</option>
              </Select>
            </FormControl>

            <Divider />

            <Box>
              <Text fontSize="sm" fontWeight="semibold" mb={1}>Company Return Address</Text>
              <Text fontSize="xs" color="gray.500" mb={3}>
                Used on every postcard unless the sender has a personal return address below.
                USPS recommends a return address on First Class mail.
              </Text>
              <ReturnAddressFields value={companyAddress} onChange={setCompanyAddress} disabled={false} />
            </Box>

            <Button size="sm" colorScheme="blue" alignSelf="flex-start" onClick={saveCompany} isLoading={savingCompany}>
              Save Company Settings
            </Button>
          </VStack>
        </Box>
      )}

      {isAdmin && (
        <Box bg={sectionBg} borderRadius="lg" p={4}>
          <Heading size="sm" mb={1}>User Access (admin)</Heading>
          <Text fontSize="xs" color="gray.500" mb={3}>
            PostcardMania is off by default. CRM Admins always have access; enable it per user below.
            Enabled users can send postcards from bulk actions and campaigns.
          </Text>
          <Box maxH="320px" overflowY="auto" borderWidth="1px" borderColor="inherit" borderRadius="md">
            <Table size="sm" variant="simple">
              <Thead position="sticky" top={0} bg={sectionBg} zIndex={1}>
                <Tr>
                  <Th>Name</Th>
                  <Th>Email</Th>
                  <Th>Postcard Access</Th>
                </Tr>
              </Thead>
              <Tbody>
                {crmUsers.map((user) => (
                  <Tr key={user.uid}>
                    <Td fontWeight="medium" fontSize="sm">{user.name}</Td>
                    <Td fontSize="sm">{user.email}</Td>
                    <Td>
                      {user.isCrmAdmin ? (
                        <Badge colorScheme="green" fontSize="xs">Admin — always on</Badge>
                      ) : (
                        <HStack spacing={2}>
                          <Switch
                            colorScheme="green"
                            isChecked={user.postcardEnabled}
                            onChange={(e) => togglePostcardAccess(user, e.target.checked)}
                          />
                          <Text fontSize="xs" fontWeight="semibold" color={user.postcardEnabled ? 'green.600' : 'gray.500'}>
                            {user.postcardEnabled ? 'Enabled' : 'Disabled'}
                          </Text>
                        </HStack>
                      )}
                    </Td>
                  </Tr>
                ))}
                {crmUsers.length === 0 && (
                  <Tr>
                    <Td colSpan={3}>
                      <Text fontSize="sm" color="gray.500">Loading CRM users…</Text>
                    </Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
          </Box>
        </Box>
      )}

      <Box bg={sectionBg} borderRadius="lg" p={4}>
        <Heading size="sm" mb={1}>Personal Return Address</Heading>
        <Text fontSize="xs" color="gray.500" mb={3}>
          Optional override — postcards you send use this instead of the company return address.
          Leave the street empty to use the company default.
        </Text>
        <ReturnAddressFields value={personalAddress} onChange={setPersonalAddress} disabled={false} />
        <Button size="sm" colorScheme="blue" mt={4} onClick={savePersonal} isLoading={savingPersonal}>
          Save Return Address
        </Button>
      </Box>

      {!isAdmin && (
        <Alert status="info" borderRadius="md" fontSize="sm">
          <AlertIcon />
          Environment, company return address, and API keys are managed by CRM admins.
        </Alert>
      )}
    </VStack>
  );
}
